// src/employee/pages/HrChatbox.js
import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaPaperPlane, FaSearch, FaTimes, FaUserCircle } from "react-icons/fa";
import { collection, onSnapshot, query, where, doc, updateDoc, serverTimestamp, arrayUnion, setDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useEmployeeAuth } from "../auth/EmployeeAuthContext";
import { format, isToday, isYesterday, startOfDay } from "date-fns";

const formatDateHeader = (date) => {
  if (isToday(date)) return "Today";
  if (isYesterday(date)) return "Yesterday";
  return format(date, "EEEE, MMM d");
};

export default function HrChatbox({ isOpen, onClose }) {
  const { user } = useEmployeeAuth();
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [chatInput, setChatInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [chat, setChat] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [showChat, setShowChat] = useState(false);
  const chatScrollRef = useRef(null);

  // Load employees
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "employees"), (snap) => {
      const emps = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setEmployees(emps.filter(e => e.email !== user.email));
    });
    return unsub;
  }, [user.email]);

  // Load chat when employee selected
  useEffect(() => {
    if (!selectedEmp) return;

    const chatRef = doc(db, "hrChats", `${user.email}_${selectedEmp.email}`);
    const unsub = onSnapshot(chatRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setChat(data.messages || []);
      } else {
        setChat([]);
      }
    });

    return unsub;
  }, [selectedEmp, user.email]);

  // Load unread counts
  useEffect(() => {
    const unsubs = employees.map(emp => {
      const chatId = `${user.email}_${emp.email}`;
      const chatRef = doc(db, "hrChats", chatId);
      return onSnapshot(chatRef, (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          const unread = data.unread?.[user.email] ? (data.messages?.filter(m =>
            m.sender === emp.email && !data.chatSeen?.[user.email + "_" + m.timestamp]
          ).length || 0) : 0;
          setUnreadCounts(prev => ({ ...prev, [emp.email]: unread }));
        }
      });
    });
    return () => unsubs.forEach(u => u());
  }, [employees, user.email]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [chat]);

const sendMessage = async () => {
  if (!chatInput.trim() || !selectedEmp || isSending) return;

  const msg = chatInput.trim();
  setChatInput("");
  setIsSending(true);

  const chatId = `${user.email}_${selectedEmp.email}`;
  const chatRef = doc(db, "hrChats", chatId);

  const newMsg = {
    sender: user.email,
    message: msg,
    timestamp: new Date().toISOString(),
  };

  try {
    // Use setDoc with merge to create if not exists
    await setDoc(chatRef, {
      participants: [user.email, selectedEmp.email],
      messages: arrayUnion(newMsg),
      lastMessageAt: serverTimestamp(),
      unread: { [selectedEmp.email]: true },
      chatSeen: {}
    }, { merge: true });

    // Mark as seen by HR
    await updateDoc(chatRef, {
      [`unread.${user.email}`]: false
    });

  } catch (err) {
    console.error("Send failed:", err);
    alert("Failed to send message");
    setChatInput(msg);
  } finally {
    setIsSending(false);
  }
};
  const groupMessagesByDate = () => {
    const groups = {};
    chat.forEach(m => {
      const date = startOfDay(new Date(m.timestamp));
      const key = date.toISOString();
      if (!groups[key]) groups[key] = [];
      groups[key].push(m);
    });
    return groups;
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ x: 400 }}
      animate={{ x: 0 }}
      exit={{ x: 400 }}
      className="fixed right-0 top-0 h-full w-96 bg-gradient-to-br from-neutral-900 to-indigo-950 shadow-2xl z-50 flex flex-col"
    >
      {/* Header */}
      <div className="p-6 border-b border-white/20 flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Direct Messages</h2>
        <button onClick={onClose} className="text-white/70 hover:text-white">
          <FaTimes />
        </button>
      </div>

      {/* Employee List */}
      {!selectedEmp ? (
        <div className="flex-1 overflow-y-auto p-4">
          <div className="mb-4">
            <div className="relative">
              <FaSearch className="absolute left-3 top-3 text-white/50" />
              <input
                placeholder="Search employees..."
                className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50"
              />
            </div>
          </div>
          {employees.map(emp => {
            const chatId = `${user.email}_${emp.email}`;
            return (
              <motion.div
                key={emp.email}
                whileHover={{ backgroundColor: "rgba(255,255,255,0.1)" }}
                onClick={() => setSelectedEmp(emp)}
                className="flex items-center gap-3 p-3 rounded-xl cursor-pointer"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center text-white font-bold">
                  {emp.name?.[0] || emp.email[0].toUpperCase()}
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">{emp.name || emp.email.split("@")[0]}</p>
                  <p className="text-white/50 text-xs">{emp.designation || "Employee"}</p>
                </div>
                {unreadCounts[emp.email] > 0 && (
                  <span className="w-6 h-6 bg-red-500 rounded-full text-xs text-white flex items-center justify-center font-bold">
                    {unreadCounts[emp.email]}
                  </span>
                )}
              </motion.div>
            );
          })}
        </div>
      ) : (
        <>
          {/* Chat Header */}
          <div className="p-4 border-b border-white/20 flex items-center gap-3">
            <button onClick={() => setSelectedEmp(null)} className="text-white/70 hover:text-white">
              <FaTimes />
            </button>
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center text-white font-bold">
              {selectedEmp.name?.[0] || selectedEmp.email[0].toUpperCase()}
            </div>
            <div>
              <p className="text-white font-medium">{selectedEmp.name || selectedEmp.email.split("@")[0]}</p>
              <p className="text-white/50 text-xs">Active</p>
            </div>
          </div>

          {/* Messages */}
          <div ref={chatScrollRef} className="flex-1 overflow-y-auto p-4 space-y-6">
            {Object.entries(groupMessagesByDate()).map(([dateKey, msgs]) => (
              <div key={dateKey}>
                <div className="text-center text-white/50 text-xs mb-4">
                  {formatDateHeader(new Date(dateKey))}
                </div>
                {msgs.map((m, i) => {
                  const isMe = m.sender === user.email;
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${isMe ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-xs px-4 py-2 rounded-2xl text-sm ${
                          isMe
                            ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
                            : "bg-white/10 text-white border border-white/20"
                        }`}
                      >
                        <p>{m.message}</p>
                        <p className="text-xs opacity-70 mt-1">
                          {new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Input */}
          <div className="p-4 border-t border-white/20">
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Type a message..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                className="flex-1 bg-white/10 border border-white/20 rounded-3xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
              <button
                onClick={sendMessage}
                disabled={!chatInput.trim() || isSending}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 p-3 rounded-3xl hover:scale-110 transition disabled:opacity-50"
              >
                <FaPaperPlane className="text-white" />
              </button>
            </div>
          </div>
        </>
      )}
    </motion.div>
  );
}